import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { aposPrimeiroGesto } from 'src/app/shared/utils/terceiros-fora-do-boot';
import { ClarityService } from './clarity.service';

/**
 * Despachante dos eventos de funil do app.
 *
 * Apesar do nome, este serviço não fala só com o OpenPanel: todo `track()` é
 * ESPELHADO no Microsoft Clarity, como custom event e custom tag, para que uma
 * gravação de sessão possa ser filtrada pelas mesmas etapas que o funil mede
 * ("colocou no carrinho", "finalizou pedido"). O espelho mora aqui, e não nas
 * páginas, porque esse é o único jeito de os dois painéis não dessincronizarem:
 * evento novo entra em um `openPanel.track()` e chega aos dois sem ninguém
 * precisar lembrar do segundo. O `clarity.service.ts` explica o que cada evento
 * vira do lado do Clarity; `docs/clarity.md`, o resto.
 */
@Injectable({ providedIn: 'root' })
export class OpenPanelService {
	private initialized = false;

	constructor(private clarity: ClarityService) {}

	/**
	 * ⚠️ A guarda de plataforma é o que impede o SSR de parar no meio.
	 *
	 * Este método usa `window` e `document` direto, e no render do servidor os dois não existem.
	 * O `init()` é chamado de dentro do `loadByURL` (restaurant.service), onde um `catch` engole
	 * qualquer erro — então a exceção não aparecia como exceção: a montagem simplesmente parava
	 * ANTES do `getFeaturedItems`, e o `menu.page` estourava depois em
	 * `menu.featuredItems.some(...)` com `featuredItems` undefined. Resultado no HTML: nenhum
	 * produto e 3 anotações de hidratação em vez de 45.
	 *
	 * POR QUE O YELLOW NUNCA PEGOU: o chamador só chama quando `!isGreen`, e `isGreen` é
	 * `environment.apiURL.includes('green')`. O yellow aponta para o backend GREEN, então lá
	 * `isGreen` é true e este caminho nunca rodava. Em produção `apiURL` é `api.multipedidos...`,
	 * e roda. Foi o render de fumaça do deploy de produção que achou, em 22/09/2026.
	 *
	 * A guarda fica AQUI, e não no chamador, porque o serviço é quem sabe que depende de browser —
	 * e assim vale para todo chamador futuro. É o mesmo raciocínio do `canEmit()` do
	 * TraceTransportService.
	 */
	init(restaurantId: number, flowType: string): void {
		if (typeof window === 'undefined' || typeof document === 'undefined') return;
		if (this.initialized || !restaurantId) return;
		this.initialized = true;

		// Carimba a sessão do Clarity com a MESMA identificação que vai nas
		// propriedades globais do OpenPanel, logo abaixo.
		this.clarity.identificarLoja(restaurantId, flowType);

		const w = window as any;
		w.op = w.op || function (...args: any[]) { (w.op.q = w.op.q || []).push(args); };

		w.op('init', {
			apiUrl: environment.openPanel.apiUrl,
			clientId: environment.openPanel.clientId,
			trackScreenViews: true,
			trackOutgoingLinks: true,
			trackAttributes: true,
		});
		w.op('setGlobalProperties', { restaurant_id: String(restaurantId), flow_type: flowType });

		// O SDK só é BAIXADO no primeiro gesto. As chamadas acima já funcionam sem ele: `w.op`
		// é uma função de fila (`w.op.q`), e o `op1.js` drena essa fila ao chegar — por isso
		// nenhum evento se perde. Medido em 18/09/2026 na cascata do boot: o script entrava em
		// ~1,9 s, exatamente durante a montagem do cardápio, competindo por rede e CPU com a
		// primeira tela. O agendador também dispara ao esconder/fechar a aba, então quem sair
		// sem tocar em nada ainda tem os eventos enviados. Ver terceiros-fora-do-boot.ts.
		aposPrimeiroGesto(() => {
			const script = document.createElement('script');
			script.src = 'assets/js/op1.js';
			script.async = true;
			script.defer = true;
			document.body.appendChild(script);
		});
	}

	track(name: string, properties: { [key: string]: any } = {}): void {
		// Mesma razão do `init()`: `window` não existe no render do servidor, e `window as any`
		// lança ReferenceError — não devolve undefined. Como a chamada nasce dentro de um `catch`
		// que engole tudo, o efeito seria a montagem parando no meio, sem erro visível.
		if (typeof window === 'undefined') return;

		// O espelho vem ANTES e FORA da guarda do `op`: o Clarity tem as próprias
		// guardas (e é no-op fora de produção), e uma sessão em que o SDK do
		// OpenPanel não chegou é exatamente a que vale assistir.
		this.clarity.eventoDoFunil(name, properties);

		const w = window as any;
		// op1.js troca w.op por um Proxy cujo alvo é uma função (garante typeof === 'function').
		// Se revendorizar o SDK, confirmar que esse invariante continua valendo.
		if (!this.initialized || typeof w.op !== 'function') return;
		w.op('track', name, properties);
	}
}
